import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, Clock, Dumbbell, RefreshCw, Play, Check, 
  Heart, Zap, Target, Moon, ChevronRight, CheckCircle, Flame,
  Droplets, Coffee, Sun
} from "lucide-react";
import HydrationTracker from "@/components/HydrationTracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklySchedule = () => {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [completedDrills, setCompletedDrills] = useState({});
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSchedule();
    fetchProgress();
  }, []);

  const fetchSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/schedule/weekly`, { headers });
      setSchedule(response.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/schedule/progress`, { headers });
      setProgress(response.data);
      
      // Build completed drills map by date
      const completed = {};
      for (const comp of response.data.completions || []) {
        completed[comp.date] = new Set(comp.completed_drills || []);
      }
      setCompletedDrills(completed);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/schedule/regenerate`, {}, { headers });
      setSchedule(response.data);
      toast.success('Schedule regenerated with new drills!');
    } catch (error) {
      toast.error('Failed to regenerate schedule');
    } finally {
      setRegenerating(false);
    }
  };

  const markDrillComplete = async (drillId, date) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/schedule/complete-drill`, { drill_id: drillId, date }, { headers });
      
      // Update local state
      setCompletedDrills(prev => {
        const updated = { ...prev };
        if (!updated[date]) updated[date] = new Set();
        updated[date].add(drillId);
        return updated;
      });
      
      toast.success('Drill completed!', {
        action: { label: 'Undo', onClick: () => resetDrillComplete(drillId, date) },
        duration: 8000,
      });
      fetchProgress();
    } catch (error) {
      toast.error('Failed to mark drill complete');
    }
  };

  const resetDrillComplete = async (drillId, date) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/schedule/reset-drill`, { drill_id: drillId, date }, { headers });
      setCompletedDrills(prev => {
        const updated = { ...prev };
        if (updated[date]) updated[date].delete(drillId);
        return updated;
      });
      toast.success('Drill reset');
      fetchProgress();
    } catch {
      toast.error('Failed to reset drill');
    }
  };

  const isDrillComplete = (drillId, date) => {
    return completedDrills[date]?.has(drillId) || false;
  };

  const startDrill = (drill) => {
    navigate('/engine', { state: { selectedDrill: drill } });
  };

  const getFocusIcon = (focus) => {
    if (!focus) return <Dumbbell className="w-4 h-4" />;
    switch (focus.toLowerCase()) {
      case 'stamina': return <Heart className="w-4 h-4" />;
      case 'explosiveness': return <Zap className="w-4 h-4" />;
      case 'agility': return <Target className="w-4 h-4" />;
      case 'recovery': return <Moon className="w-4 h-4" />;
      default: return <Dumbbell className="w-4 h-4" />;
    }
  };

  const getFocusColor = (focus) => {
    if (!focus) return 'text-gray-500 bg-gray-500/10 border-gray-500/30';
    switch (focus.toLowerCase()) {
      case 'stamina': return 'text-red-500 bg-red-500/10 border-red-500/30';
      case 'explosiveness': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30';
      case 'agility': return 'text-blue-500 bg-blue-500/10 border-blue-500/30';
      case 'recovery': return 'text-purple-500 bg-purple-500/10 border-purple-500/30';
      default: return 'text-gray-500 bg-gray-500/10 border-gray-500/30';
    }
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Generating your schedule...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-black text-white tracking-tighter uppercase">
              Weekly Schedule
            </h1>
            {schedule?.phase_name && schedule?.week_start ? (
              <p className="text-muted-foreground mt-1">
                {schedule.phase_name} • Week of {new Date(schedule.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            ) : (
              <p className="text-muted-foreground mt-1">Your personalized training plan</p>
            )}
          </div>
          <Button
            onClick={handleRegenerate}
            disabled={regenerating}
            variant="outline"
            className="border-primary/50 text-primary hover:bg-primary/10"
            data-testid="regenerate-schedule-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Regenerating...' : 'New Schedule'}
          </Button>
        </div>

        {/* Empty / error state when schedule failed to load */}
        {!schedule?.schedule?.length && (
          <Card className="bg-card border-border" data-testid="schedule-empty-state">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-primary/40 mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">No schedule yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Tap <strong>New Schedule</strong> to generate this week's training plan.
              </p>
              <Button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="bg-primary hover:bg-primary/80"
                data-testid="generate-schedule-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                Generate Schedule
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Weekly Summary */}
        {schedule?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="weekly-summary">
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Calendar className="w-5 h-5 mx-auto text-primary mb-2" />
                <p className="text-2xl font-bold text-white">{schedule.summary.training_days}</p>
                <p className="text-xs text-muted-foreground">Training Days</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Moon className="w-5 h-5 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold text-white">{schedule.summary.rest_days}</p>
                <p className="text-xs text-muted-foreground">Rest Days</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Clock className="w-5 h-5 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-white">{schedule.summary.total_minutes}</p>
                <p className="text-xs text-muted-foreground">Total Minutes</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Dumbbell className="w-5 h-5 mx-auto text-accent mb-2" />
                <p className="text-2xl font-bold text-white">{progress?.drills_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Drills Done</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Flame className="w-5 h-5 mx-auto text-orange-500 mb-2" />
                <p className="text-2xl font-bold text-white">{progress?.current_streak || 0}</p>
                <p className="text-xs text-muted-foreground">Day Streak</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
              <CardContent className="p-4 text-center">
                <Droplets className="w-5 h-5 mx-auto text-cyan-400 mb-2" />
                <p className="text-2xl font-bold text-cyan-400">128oz</p>
                <p className="text-xs text-muted-foreground">Daily Water Goal</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Phase Focus */}
        {schedule?.phase_focus && (
          <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/30">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                <span className="text-primary font-semibold">Phase Focus:</span> {schedule.phase_focus}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Schedule Grid */}
        <div className="space-y-3" data-testid="schedule-grid">
          {schedule?.schedule?.map((day, index) => (
            <Card 
              key={day.day}
              className={`bg-card border-border overflow-hidden transition-all ${
                isToday(day.date) ? 'ring-2 ring-primary' : ''
              }`}
              data-testid={`day-${day.day.toLowerCase()}`}
            >
              <div className={`p-4 ${day.is_rest_day ? 'bg-purple-500/5' : ''}`}>
                {/* Day Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${getFocusColor(day.focus)}`}>
                      {getFocusIcon(day.focus)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-white">{day.day}</h3>
                        {isToday(day.date) && (
                          <Badge className="bg-primary text-white text-xs">TODAY</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={getFocusColor(day.focus)}>
                      {day.focus}
                    </Badge>
                    {!day.is_rest_day && (
                      <p className="text-xs text-muted-foreground mt-1">{day.total_minutes} min</p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <p className="text-sm text-muted-foreground mb-3">{day.message}</p>

                {/* Drills List */}
                {!day.is_rest_day && day.drills.length > 0 && (
                  <div className="space-y-2">
                    {day.drills.map((drill, drillIndex) => {
                      const isComplete = isDrillComplete(drill.id, day.date);
                      return (
                        <div 
                          key={drill.id}
                          className={`flex items-center justify-between p-3 bg-background rounded-sm border transition-colors group ${
                            isComplete ? 'border-green-500/50 bg-green-500/5' : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Completion Checkbox - toggles complete/reset */}
                            <button
                              onClick={() =>
                                isComplete
                                  ? resetDrillComplete(drill.id, day.date)
                                  : markDrillComplete(drill.id, day.date)
                              }
                              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors group ${
                                isComplete 
                                  ? 'bg-green-500 text-white hover:bg-red-500' 
                                  : 'bg-primary/20 text-primary hover:bg-primary/40'
                              }`}
                              data-testid={`complete-drill-${drill.id}`}
                              title={isComplete ? "Click to reset" : "Click to mark complete"}
                            >
                              {isComplete ? (
                                <>
                                  <CheckCircle className="w-4 h-4 group-hover:hidden" />
                                  <RefreshCw className="w-4 h-4 hidden group-hover:block" />
                                </>
                              ) : (
                                <span className="text-xs font-bold">{drillIndex + 1}</span>
                              )}
                            </button>
                            <div className="min-w-0">
                              <p className={`font-medium text-sm truncate ${isComplete ? 'text-green-400 line-through' : 'text-white'}`}>
                                {drill.name}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{Math.floor(drill.duration_seconds / 60)}:{(drill.duration_seconds % 60).toString().padStart(2, '0')} work</span>
                                <span>•</span>
                                <span>{drill.sets} sets</span>
                                <span>•</span>
                                <Badge variant="outline" className="text-xs px-1 py-0">
                                  {drill.difficulty}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isComplete && (
                              <Badge className="bg-green-500/20 text-green-400 text-xs">Done</Badge>
                            )}
                            <Button
                              size="sm"
                              onClick={() => startDrill(drill)}
                              className={`bg-primary/20 hover:bg-primary text-primary hover:text-white transition-opacity ${
                                isComplete ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'
                              }`}
                              data-testid={`start-drill-${drill.id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Rest Day Content */}
                {day.is_rest_day && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center p-6 bg-purple-500/5 rounded-lg border border-purple-500/20">
                      <div className="text-center">
                        <Moon className="w-10 h-10 mx-auto text-purple-500 mb-3" />
                        <p className="text-lg font-bold text-white mb-1">Rest & Recover</p>
                        <p className="text-sm text-muted-foreground">Your muscles grow during rest, not during training</p>
                      </div>
                    </div>
                    
                    {/* Rest Day Recovery Checklist */}
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Droplets className="w-5 h-5 text-cyan-400" />
                          <span className="font-semibold text-cyan-400">Hydration Goal</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Stay hydrated throughout the day to support recovery.</p>
                      </div>
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Sun className="w-5 h-5 text-yellow-400" />
                          <span className="font-semibold text-yellow-400">Active Recovery</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Move your body lightly to feel fresh tomorrow.</p>
                      </div>
                      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Moon className="w-5 h-5 text-blue-400" />
                          <span className="font-semibold text-blue-400">Sleep</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Get quality sleep tonight — recovery happens while you rest.</p>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Coffee className="w-5 h-5 text-green-400" />
                          <span className="font-semibold text-green-400">Fuel Up</span>
                        </div>
                        <p className="text-sm text-muted-foreground">Eat balanced meals to support your training.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Start Day Button */}
                {!day.is_rest_day && isToday(day.date) && day.drills.length > 0 && (
                  <Button
                    onClick={() => startDrill(day.drills[0])}
                    className="w-full mt-3 bg-primary hover:bg-primary/90"
                    data-testid="start-today-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Today's Workout
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Training Tips */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-white">Training Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Droplets className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-cyan-400">Hydration:</strong> Stay hydrated — drink before, during, and after every workout.</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Explosiveness days:</strong> Train when fresh. Low volume, high intensity.</span>
              </li>
              <li className="flex items-start gap-2">
                <Heart className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Stamina days:</strong> Build your engine. Moderate intensity, higher volume.</span>
              </li>
              <li className="flex items-start gap-2">
                <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Agility days:</strong> Focus on quality reps. Speed and precision over fatigue.</span>
              </li>
              <li className="flex items-start gap-2">
                <Moon className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Rest days:</strong> Recover well — sleep and light movement help you train harder tomorrow.</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WeeklySchedule;
